import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { login } from 'components';
import { card } from 'components/common';
import { useTranslation } from 'react-i18next';
import { useStateContext } from 'store/state';
import { ActionType } from 'store/reducer';
import { getBrowserDetails } from 'utils/helpers/browser';
import * as dal from 'dal';
import styled from 'styled-components';
import { STYLES } from 'utils/constants/styles';
import { hashSha256 } from 'utils/helpers/hash';

const { TextCard } = card;
const { LoginForm } = login;

type Props = {
    loginType: LoginType;
};

const COOKIES_STATUS = 3;

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding-top: ${STYLES.CONTENT_PADDING_TOP};
`;

const LoginView: React.FC<Props> = ({ loginType }) => {
    const { dispatch } = useStateContext();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const { t } = useTranslation(['common', 'translation']);
    const history = useHistory();

    const redirectToScreening = () => history.push(`/screening`);

    const redirectToPatientPortal = () => {
        history.push(`/screenings`);
    };

    const afterLoginAuditUserBrowserDetails = async () => {
        const { name, os, version } = getBrowserDetails();
        const {
            screen: { availHeight, availWidth },
        } = window;
        await dal.audit.saveClientBrowserDetails({
            browserName: name || '',
            browserVersion: version || '',
            os: os || '',
            resolutionX: availWidth,
            resolutionY: availHeight,
        });
    };

    const loginToResultPortal = async (values: ILoginAttempt) => {
        setLoading(true);
        await dal.auth
            .login(values)
            .then(() =>
                dispatch({
                    type: ActionType.SIGN_IN,
                })
            )
            .then(redirectToScreening)
            .then(afterLoginAuditUserBrowserDetails)
            .catch(err => {
                if (err?.response?.data?.status === COOKIES_STATUS) {
                    return setError(t('translation:cookies_error'));
                }

                return setError(t('translation:login_error'));
            })
            .finally(async () => setLoading(false));
    };

    const loginToPatientPortal = async (values: ILoginAttempt) => {
        if (!values.password) return;

        setLoading(true);
        dal.auth
            .loginAsPatient({ ...values, password: await hashSha256(values.password) })
            .then(() => dispatch({ type: ActionType.SIGN_IN }))
            .then(redirectToPatientPortal)
            .then(afterLoginAuditUserBrowserDetails)
            .catch(err => {
                if (err?.response?.data?.status === COOKIES_STATUS) {
                    return setError(t('translation:cookies_error'));
                }
                if (err?.response?.status === 403) {
                    return setError(err?.response?.data.message);
                }
                return setError(t('translation:login_error'));
            })
            .finally(async () => setLoading(false));
    };

    const onLogin = (values: ILoginAttempt) => {
        if (loginType === 'resultPortal') {
            loginToResultPortal(values);
            return;
        }
        loginToPatientPortal(values);
    };

    return (
        <StyledContainer>
            <LoginForm loading={loading} onFinish={onLogin} error={error} type={loginType} />
            <TextCard
                title={t('translation:info_card_title')}
                content={<p>{t('translation:info_card_content')}</p>}
                footer={
                    <>
                        <p>
                            <em>{t('common:management_board')}</em> <br />
                        </p>
                    </>
                }
            />
        </StyledContainer>
    );
};

export default LoginView;
